import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Alert, Input } from 'antd';
import { loadUser } from '../../ducks/Auth/actions';
import { ASSESSMENT_TYPES } from '../../config/assessmentConfigs';
import '../../presentational/ShortAssessment/index.css';
import './quickSetup.css';

const MEASUREMENT_OPTIONS = [
  { value: true, label: 'Metric (kg, cm)' },
  { value: false, label: 'Imperial (lb, ft/in)' },
];

const QuickSetup = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const auth = useSelector((state) => state.auth);
  const [step, setStep] = useState(1);
  // Default to Imperial (lb, ft/in) — the primary audience is US-based.
  const [isMetric, setIsMetric] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!auth.loading && !auth.isAuthenticated) {
      history.push('/');
      return;
    }

    if (!auth.user) return;

    if (
      auth.user.role === 'trainer' ||
      auth.user.assessmentType !== ASSESSMENT_TYPES.NONE
    ) {
      history.push('/dashboard/home');
      return;
    }

    if (auth.user.isAssessmentCompleted) {
      history.push('/dashboard/home');
    }
  }, [auth, history]);

  const progressPercent = useMemo(() => (step === 1 ? 50 : 100), [step]);

  const handleContinue = () => {
    setError('');
    setStep(2);
  };

  const handleSubmit = async () => {
    setError('');
    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Password and confirm password must match.');
      return;
    }

    const token = window.localStorage.token;
    if (!token) {
      setError('Session expired. Please login again.');
      history.push('/');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/quick-setup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token,
          },
          body: JSON.stringify({
            isMetric,
            newPassword,
            confirmPassword,
          }),
        }
      );
      const data = await res.json();

      if (!res.ok) {
        const firstError =
          data?.errors?.[0]?.msg || data?.msg || 'Unable to complete setup.';
        throw new Error(firstError);
      }

      dispatch(loadUser());
      // Land on the shared "you're all set → download the app" screen (the same
      // one the full/basic assessment flow uses) so no-assessment clients get the
      // app-download nudge too, then continue to the dashboard from there.
      history.push({
        pathname: '/assessment-completed',
        state: { fromAssessment: true },
      });
    } catch (err) {
      setError(err.message || 'Unable to complete setup.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='content short-assessment quick-setup-page'>
      <div className='quick-setup-card'>
        <div className='quick-setup-header'>
          <span className='quick-setup-eyebrow'>Quick setup</span>
          <span className='quick-setup-step-meta'>
            Step {step} of 2 · {progressPercent}%
          </span>
        </div>
        <div className='quick-setup-progress-track'>
          <div
            className='quick-setup-progress-fill'
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <div className='quick-setup-content'>
          <h1 className='quick-setup-title'>
            {auth?.user?.name ? `Welcome, ${auth.user.name}!` : 'Welcome!'}
          </h1>
          <p className='quick-setup-sub'>
            Two quick steps and you're in.
          </p>

          {error ? (
            <Alert
              type='error'
              message={error}
              showIcon
              style={{ marginBottom: 16 }}
            />
          ) : null}

          {step === 1 ? (
            <div className='quick-setup-step'>
              <p className='quick-setup-step-label'>
                Choose your measurement system
              </p>
              {/* Segmented pill mirrors the All / Default / Custom toggle
                  used in the V2 admin filter bars. */}
              <div className='quick-setup-segmented'>
                {MEASUREMENT_OPTIONS.map((opt) => (
                  <button
                    key={String(opt.value)}
                    type='button'
                    onClick={() => setIsMetric(opt.value)}
                    className={`btn btn-sm ${
                      isMetric === opt.value ? 'btn-secondary' : 'btn-ghost'
                    }`}
                    style={{ boxShadow: 'none' }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className='quick-setup-actions'>
                <button
                  type='button'
                  className='btn btn-grad'
                  onClick={handleContinue}
                >
                  Continue
                </button>
              </div>
            </div>
          ) : (
            <div className='quick-setup-step'>
              <p className='quick-setup-step-label'>Set your password</p>
              <div className='form-section'>
                <p className='label'>New password</p>
                <Input.Password
                  placeholder='At least 8 characters'
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div className='form-section'>
                <p className='label'>Confirm password</p>
                <Input.Password
                  placeholder='Re-enter your password'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <div className='quick-setup-actions'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => setStep(1)}
                >
                  Back
                </button>
                <button
                  type='button'
                  className='btn btn-grad'
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Saving…' : 'Complete setup'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickSetup;
